import React from 'react';
import { Body1, Body5, Container, Dialog, DialogButtonRow, TextField } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { ISSUER_PATTERN, rerunRun } from '@services/fmdc';

// The rerun confirm, shared by the runs list and the run detail. A `ConfirmModal` body cannot hold
// inputs, so this is the same Dialog with the issuer fields folded in: a run uploaded without an
// issuer gets one here, which is the only way its decisions start carrying over.
//
// Parent controls mounting: `{open && <RerunConfirm run={run} onDone={…} onCancel={…} />}`.
// `onDone(run)` receives the 202 body (the run, now pending).

export const isIssuerValid = (value) => !value || ISSUER_PATTERN.test(value);

// The id pattern is the API's rule for `issuer_id`. Nothing constrains `issuer_name` beyond the
// same length, and a company name carries ampersands, apostrophes and accents, so only the length
// is checked here.
export const ISSUER_NAME_MAX = 64;
export const isIssuerNameValid = (value) => !value || value.length <= ISSUER_NAME_MAX;

export const RerunConfirm = ({ run, onDone, onCancel }) => {
  const hasIssuer = !!run?.issuer_id;
  const [issuerId, setIssuerId] = React.useState(run?.issuer_id || '');
  const [issuerName, setIssuerName] = React.useState(run?.issuer_name || '');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const issuerInvalid = !isIssuerValid(issuerId.trim());
  const nameInvalid = !isIssuerNameValid(issuerName.trim());

  const confirm = async () => {
    if (busy || issuerInvalid || nameInvalid) return;
    setBusy(true);
    setError(null);
    const result = await rerunRun(run.id, {
      issuerId: hasIssuer ? undefined : issuerId.trim() || undefined,
      issuerName: hasIssuer ? undefined : issuerName.trim() || undefined,
    });
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    onDone(result.run);
  };

  const cancel = () => {
    if (busy) return;
    onCancel();
  };

  const title = `Rerun run #${run?.id}`;

  return (
    <Dialog label={title} onDismiss={cancel} maxWidth="480px">
      <Body1 bold>{title}</Body1>
      <Container top={12}>
        <Body5>
          A rerun replaces this run's rows and applies every saved reviewer decision. Mapped rows appear among the
          values; rejected rows are dropped.
        </Body5>
      </Container>
      {!hasIssuer && (
        <>
          <Container top={16}>
            <Body5>Set an issuer so decisions carry over to later uploads of the same model.</Body5>
          </Container>
          <Container top={12}>
            <TextField
              label="Issuer id"
              value={issuerId}
              onChange={setIssuerId}
              invalid={issuerInvalid}
              hint={issuerInvalid ? '1 to 64 characters: letters, digits, space, . _ - or /' : 'Optional, e.g. 86910'}
              placeholder="86910"
            />
          </Container>
          <Container top={12}>
            <TextField
              label="Issuer name"
              value={issuerName}
              onChange={setIssuerName}
              invalid={nameInvalid}
              hint={nameInvalid ? 'Up to 64 characters' : 'Optional'}
            />
          </Container>
        </>
      )}
      {error && (
        <Container top={12}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}
      <Container top={24}>
        <DialogButtonRow>
          <TransparentFlatButton onClick={cancel} disabled={busy}>
            Cancel
          </TransparentFlatButton>
          <FlatButton onClick={confirm} disabled={busy || issuerInvalid || nameInvalid}>
            {busy ? 'Starting…' : 'Rerun'}
          </FlatButton>
        </DialogButtonRow>
      </Container>
    </Dialog>
  );
};
